import { GTM } from "./gtm"
import { GA4 } from "./ga4"
import { Facebook } from "./facebook"
import { Tiktok } from "./tiktok";
import ConsentCountries from '@/components/cookie/consentCountries';
import { COOKIE, getClientCookie } from "#shared/utils/cookie";
import { type SiteData } from '#shared/types';

type IntegrationPayload = Pick<SiteData, 'integrations' | 'countryCode' | 'preview' | 'pageSlug'>;

export const Integration = {
  siteData: null as IntegrationPayload | null,
  isProd: () => useRuntimeConfig().public.NUXT_PROD,
  init(payload: IntegrationPayload) {
    this.siteData = Object.assign({
      integrations: {},
      preview: false,
      countryCode : '',
      pageSlug: '',
    }, payload) as IntegrationPayload;

    const headScripts = {
      script: [] as any[],
      noscript: [] as any[]
    };

    const cookieConsent = getClientCookie(COOKIE.COOKIE_CONSENT);
    const countryCode = this.siteData?.countryCode || '';
    const isDefaultGranted = !ConsentCountries.includes(countryCode);

    if (!this.siteData || this.siteData?.preview) return headScripts;
    
    const { gtmData, gaData, googleAds } = this.siteData?.integrations || {};

    if (gtmData?.isActive && !!gtmData?.gtmCode) {
      const scripts = GTM.initOnSSR({
        id: gtmData?.gtmCode,
        domain: gtmData?.gtmDomain,
        granted: isDefaultGranted,
        debug: !this.isProd(),
      });

      headScripts.script.push(...scripts.script);
      headScripts.noscript.push(...scripts.noscript);
    }

    if (
      (gaData?.isActive && !!gaData?.gaCode) ||
      (googleAds?.isActive && !!googleAds?.gTag)
    ) {
      const scripts = GA4.initOnSSR({
        googleAds,
        gaCode: gaData?.gaCode,
        granted: isDefaultGranted,
        debug: !this.isProd(),
      });

      headScripts.script.push(...scripts.script);
    }

    // Init if user access granted on cookie popup
    if (cookieConsent) {
      const metaScripts = this.meta(true);
      const tiktokScripts = this.tiktok(true);

      headScripts.script.push(...metaScripts.script, ...tiktokScripts.script);
      headScripts.noscript.push(...metaScripts.noscript, ...tiktokScripts.noscript);
    }

    return headScripts;
  },
  meta(ssr = false) {
    const { facebookData } = this.siteData?.integrations || {};

    const headScripts = {
      script: [] as any[],
      noscript: [] as any[]
    };

    if (facebookData?.isActive && facebookData?.integrationType !== 'capi' && !!facebookData?.pixelId) {
      const payload = {
        pixelId: facebookData.pixelId,
        debug: !this.isProd(),
        pageSlug: this.siteData?.pageSlug || '',
        countryCode: this.siteData?.countryCode || ''
      };

      const scripts = ssr ? Facebook.initOnSSR(payload) : Facebook.init(payload);

      headScripts.script.push(...scripts.script);
      headScripts.noscript.push(...scripts.noscript);
    }

    return headScripts;
  },
  tiktok(ssr = false) {
    const { tiktokData } = this.siteData?.integrations || {};

    const headScripts = {
      script: [] as any[],
      noscript: [] as any[]
    };

    if (tiktokData?.isActive && tiktokData?.integrationType !== 'capi' && !!tiktokData?.pixelId) {
      const payload = {
        pixelId: tiktokData.pixelId,
        debug: !this.isProd(),
        pageSlug: this.siteData?.pageSlug || '',
        countryCode: this.siteData?.countryCode || ''
      };

      const scripts = ssr ? Tiktok.initOnSSR(payload) : Tiktok.init(payload);

      headScripts.script.push(...scripts.script);
      headScripts.noscript.push(...scripts.noscript);
    }

    return headScripts;
  }
}

export * from './gtm';
export * from './ga4';
export * from './facebook';
export * from './tiktok';
